import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { cn } from '@/lib/utils';
import { MODULE_BRANCHES, type ModuleBranchId } from '../../api/areas/modules';
import { NavTabs } from '../../components/NavTabs';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { NavigationLink } from '../../lib/navigationMemory';
import { WithIntegrationContext } from '../integrations/shared';
import { AddonsTab } from './AddonsTab';
import { BranchTab } from './BranchTab';
import { InstancePage } from './InstancePage';
import { modulesUrl } from './ui';

/** `bankfeed` is the old name of the payment tab (ADR-028): old links still open it. */
const asBranch = (raw: string | null): ModuleBranchId =>
  MODULE_BRANCHES.find((b) => b === (raw === 'bankfeed' ? 'payment' : raw)) ?? 'registry';

/**
 * Cài đặt › Module (ADR-025 §8): one tab per business branch, each with its modules, their
 * connections (instances), the providers not wrapped yet and, for domain names, the registry
 * connections; Tiện ích lists the add-ons instead (`AddonsTab`). `?branch=…` picks the tab;
 * `&module=…&instance=…` opens one connection's page, `&addon=…` one add-on.
 */
export function ModulesPage() {
  const { t } = useTranslation('modules');
  useDocumentTitle(t('page.title'));
  return (
    <>
      <PageHeader title={t('page.title')} subtitle={t('page.subtitle')} />
      <WithIntegrationContext>
        <ModulesView />
      </WithIntegrationContext>
    </>
  );
}

function ModulesView() {
  const { t } = useTranslation('modules');
  const [params] = useSearchParams();
  const branch = asBranch(params.get('branch'));
  const module = params.get('module');
  const instance = params.get('instance');
  if (module && instance) return <InstancePage key={`${branch}/${module}/${instance}`} branch={branch} module={module} instanceKey={instance} />;
  return (
    <>
      {/* 17 links before the branch: let a keyboard user jump over them. */}
      <a href="#module-branch" className="sr-only rounded-sm text-sm text-primary focus:not-sr-only focus:mb-2 focus:inline-block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
        {t('page.skipAreas')}
      </a>
      {/* Below xl one row that scrolls sideways (never wraps to two); the faded edges show a tab is cut off. */}
      <NavTabs
        level="sub"
        className="mb-3 [mask-image:linear-gradient(to_right,transparent,black_1rem,black_calc(100%_-_1rem),transparent)] xl:hidden"
        label={t('page.areas')}
        active={branch}
        tabs={MODULE_BRANCHES.map((b) => ({ id: b, label: t(`branch.${b}`), to: modulesUrl(b) }))}
      />
      <div className="xl:grid xl:grid-cols-[12rem_minmax(0,1fr)] xl:items-start xl:gap-6">
        {/* From xl every branch is in view, as a list beside the branch (same links). */}
        <nav aria-label={t('page.areas')} className="hidden xl:block">
          <ul className="grid gap-0.5">
            {MODULE_BRANCHES.map((b) => (
              <li key={b}>
                <NavigationLink
                  to={modulesUrl(b)}
                  aria-current={b === branch ? 'page' : undefined}
                  className={cn(
                    'block rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                    b === branch && 'bg-accent font-medium text-accent-foreground',
                  )}
                >
                  {t(`branch.${b}`)}
                </NavigationLink>
              </li>
            ))}
          </ul>
        </nav>
        <div id="module-branch" tabIndex={-1} className="min-w-0 outline-none">
          {/* Tiện ích: the add-ons' lifecycle and their own pages (ADR-025 §4.6), `?addon=` opens one. */}
          {branch === 'addons' ? <AddonsTab /> : <BranchTab key={branch} branch={branch} />}
        </div>
      </div>
    </>
  );
}
